/**
 * Illustrated onboarding icons — editorial hand-drawn style.
 *
 * Replaces placeholder emoji in the onboarding wizard with custom
 * SVGs matching the Kinfolk editorial aesthetic (ink strokes, minimal,
 * warm).
 *
 * Usage: <OnboardingIcon name="gluten" className="size-12" />
 */

import type { SVGProps } from "react";

type IconName =
  // Allergies
  | "gluten"
  | "lactose"
  | "egg"
  | "peanut"
  | "tree_nuts"
  | "soy"
  | "fish"
  | "shellfish"
  | "sesame"
  | "mustard"
  | "celery"
  | "sulphites"
  // Equipment
  | "stovetop"
  | "oven"
  | "microwave"
  | "blender"
  | "food_processor"
  | "instant_pot"
  | "slow_cooker"
  | "air_fryer"
  // Diets
  | "omnivore"
  | "vegetarian"
  | "vegan"
  | "pescatarian"
  // Expertise
  | "express"
  | "normal"
  | "chef"
  // Household
  | "household_1"
  | "household_2"
  | "household_3"
  | "household_4";

const ICONS: Record<IconName, (props: SVGProps<SVGSVGElement>) => JSX.Element> =
  {
    // ── Allergies ──────────────────────────────────────────────────────

    gluten: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Grain head — oval with horizontal seed rows */}
        <ellipse
          cx="24"
          cy="14"
          rx="6"
          ry="10"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M19 9h10M19 12h10M19 15h10M19 18h10"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
        {/* Stem */}
        <path
          d="M24 24V42"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Left awns */}
        <path
          d="M24 28L17 23M24 33L17 28M24 38L17 33"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Right awns */}
        <path
          d="M24 31L31 26M24 36L31 31"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    lactose: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Milk drop */}
        <path
          d="M24 8L34 28a12 12 0 01-20 0L24 8z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Shine */}
        <path
          d="M20 30a6 3 0 006 0"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
        <path
          d="M28 22C26 20 22 20 20 22"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.5"
        />
      </svg>
    ),

    egg: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Egg — narrower at top, wider at bottom */}
        <path
          d="M24 6C18 6 12 14 12 26a12 12 0 0024 0C36 14 30 6 24 6z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Highlight */}
        <path
          d="M18 16C17 18 17 22 18 24"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
        />
      </svg>
    ),

    peanut: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Top lobe */}
        <path
          d="M24 8C20 8 15 12 15 18C15 22 18 24 24 24C30 24 33 22 33 18C33 12 28 8 24 8Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Bottom lobe */}
        <path
          d="M24 24C18 24 15 26 15 30C15 36 20 40 24 40C28 40 33 36 33 30C33 26 30 24 24 24Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Waist pinch */}
        <path
          d="M15 21h5M28 21h5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Shell texture */}
        <path
          d="M18 14C20 11 24 10 28 13"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
        />
        <path
          d="M18 32C20 29 24 28 28 31"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
        />
      </svg>
    ),

    tree_nuts: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Acorn stem */}
        <path
          d="M24 16V12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Cap brim */}
        <path
          d="M12 22h24"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Cap body */}
        <path
          d="M14 22C14 16 32 16 32 22"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Cap texture lines */}
        <path
          d="M16 22V18M20 22V17M24 22V17M28 22V17M32 22V18"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.5"
        />
        {/* Acorn body */}
        <path
          d="M12 22C12 30 17 36 24 36C31 36 36 30 36 22"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),

    soy: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Edamame pod */}
        <path
          d="M12 24C12 12 18 6 24 6C30 6 36 12 36 24C36 36 30 42 24 42C18 42 12 36 12 24Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Three beans visible through pod */}
        <circle cx="24" cy="15" r="4" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="24" cy="24" r="4" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="24" cy="33" r="4" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    ),

    fish: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Body */}
        <path
          d="M6 24C6 17 12 12 22 12C32 12 40 17 40 24C40 31 32 36 22 36C12 36 6 31 6 24Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Tail fin */}
        <path
          d="M40 18L46 14V34L40 30"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Eye */}
        <circle cx="16" cy="23" r="2.5" fill="currentColor" />
        {/* Gill line */}
        <path
          d="M22 13C22 17 22 31 22 35"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.5"
        />
        {/* Dorsal fin */}
        <path
          d="M24 12C26 8 30 8 30 12"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    ),

    shellfish: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Shrimp body — C curve */}
        <path
          d="M30 6C38 10 42 18 40 26C38 34 30 40 22 40C16 40 12 36 12 30"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Rostrum / head spike */}
        <path
          d="M30 6L34 4M30 6L26 2"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        {/* Body segments */}
        <path
          d="M38 16C36 14 34 14 32 16"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
        <path
          d="M36 22C34 20 30 20 28 22"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
        <path
          d="M34 28C32 26 28 26 26 28"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
        {/* Tail fan */}
        <path
          d="M12 30L8 34M12 30L10 37M12 30L15 36"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    sesame: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Six sesame seeds scattered */}
        <ellipse
          cx="17"
          cy="13"
          rx="3"
          ry="5"
          stroke="currentColor"
          strokeWidth="1.5"
          transform="rotate(-20 17 13)"
        />
        <ellipse
          cx="31"
          cy="14"
          rx="3"
          ry="5"
          stroke="currentColor"
          strokeWidth="1.5"
          transform="rotate(20 31 14)"
        />
        <ellipse
          cx="12"
          cy="26"
          rx="3"
          ry="5"
          stroke="currentColor"
          strokeWidth="1.5"
          transform="rotate(-10 12 26)"
        />
        <ellipse
          cx="36"
          cy="26"
          rx="3"
          ry="5"
          stroke="currentColor"
          strokeWidth="1.5"
          transform="rotate(10 36 26)"
        />
        <ellipse
          cx="19"
          cy="37"
          rx="3"
          ry="5"
          stroke="currentColor"
          strokeWidth="1.5"
          transform="rotate(-15 19 37)"
        />
        <ellipse
          cx="30"
          cy="37"
          rx="3"
          ry="5"
          stroke="currentColor"
          strokeWidth="1.5"
          transform="rotate(15 30 37)"
        />
      </svg>
    ),

    mustard: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Jar cap */}
        <rect
          x="18"
          y="6"
          width="12"
          height="5"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Neck */}
        <rect
          x="20"
          y="11"
          width="8"
          height="5"
          stroke="currentColor"
          strokeWidth="1"
        />
        {/* Jar body */}
        <path
          d="M14 16h20v20a4 4 0 01-4 4H18a4 4 0 01-4-4V16Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Label lines */}
        <path
          d="M17 26h14M17 30h10"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.5"
        />
      </svg>
    ),

    celery: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Three stalks */}
        <path
          d="M16 42V24C16 18 12 12 14 8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M24 42V22C24 14 24 10 24 8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M32 42V24C32 18 36 12 34 8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Leaves at top */}
        <path
          d="M14 8C10 4 8 6 10 10"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M24 8C22 4 24 2 26 5C28 2 30 4 26 8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M34 8C38 4 40 6 38 10"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Base */}
        <path
          d="M12 42h24"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    sulphites: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Wine glass bowl */}
        <path
          d="M14 8h20C34 8 34 22 30 28C28 31 26 32 24 32C22 32 20 31 18 28C14 22 14 8 14 8Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Wine level */}
        <path
          d="M15 22C17 25 21 26 24 26C27 26 31 25 33 22"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
        />
        {/* Stem */}
        <path d="M24 32V40" stroke="currentColor" strokeWidth="1.5" />
        {/* Base */}
        <path
          d="M16 40h16"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    // ── Equipment ──────────────────────────────────────────────────────

    stovetop: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Flat cooktop surface — no legs */}
        <rect
          x="4"
          y="12"
          width="40"
          height="28"
          rx="3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Control bar */}
        <path
          d="M4 20h40"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.4"
        />
        {/* Knobs */}
        <circle cx="12" cy="16" r="2" stroke="currentColor" strokeWidth="1" />
        <circle cx="24" cy="16" r="2" stroke="currentColor" strokeWidth="1" />
        <circle cx="36" cy="16" r="2" stroke="currentColor" strokeWidth="1" />
        {/* Left burner */}
        <circle cx="16" cy="30" r="7" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="16" cy="30" r="3" stroke="currentColor" strokeWidth="1" />
        {/* Right burner */}
        <circle cx="32" cy="30" r="7" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="32" cy="30" r="3" stroke="currentColor" strokeWidth="1" />
      </svg>
    ),

    oven: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Oven body */}
        <rect
          x="6"
          y="4"
          width="36"
          height="40"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Control panel top */}
        <path d="M6 14h36" stroke="currentColor" strokeWidth="1" />
        {/* Knobs */}
        <circle
          cx="16"
          cy="9"
          r="2.5"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <circle
          cx="24"
          cy="9"
          r="2.5"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <circle
          cx="32"
          cy="9"
          r="2.5"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        {/* Door window */}
        <rect
          x="10"
          y="18"
          width="28"
          height="20"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Door handle */}
        <path
          d="M14 40h20"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Window glare */}
        <path
          d="M13 21L17 25"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.3"
        />
      </svg>
    ),

    microwave: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Body */}
        <rect
          x="4"
          y="10"
          width="40"
          height="28"
          rx="3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Door */}
        <rect
          x="8"
          y="14"
          width="22"
          height="20"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Door handle */}
        <path
          d="M28 18v12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Control panel */}
        <rect
          x="32"
          y="14"
          width="8"
          height="20"
          rx="1"
          stroke="currentColor"
          strokeWidth="1"
        />
        {/* Buttons */}
        <circle cx="36" cy="19" r="1.5" fill="currentColor" opacity="0.5" />
        <circle cx="36" cy="24" r="1.5" fill="currentColor" opacity="0.5" />
        <circle cx="36" cy="29" r="1.5" fill="currentColor" opacity="0.5" />
      </svg>
    ),

    blender: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Jar — wider at top, narrows at bottom */}
        <path
          d="M16 10h16L28 32H20L16 10Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Lid */}
        <rect
          x="14"
          y="6"
          width="20"
          height="5"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Liquid level */}
        <path
          d="M18 20h12"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
        />
        {/* Blade */}
        <path
          d="M20 32L24 30L28 32"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
        {/* Motor base */}
        <rect
          x="16"
          y="32"
          width="16"
          height="5"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <rect
          x="14"
          y="37"
          width="20"
          height="5"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),

    food_processor: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Motor base */}
        <rect
          x="10"
          y="30"
          width="28"
          height="12"
          rx="3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Bowl */}
        <path
          d="M14 16h20V30C34 36 30 38 24 38C18 38 14 36 14 30V16Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Lid rim */}
        <path
          d="M12 16h24"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Feed tube */}
        <rect
          x="20"
          y="8"
          width="8"
          height="8"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="M22 8V6H26V8" stroke="currentColor" strokeWidth="1.2" />
        {/* Blade inside */}
        <path
          d="M18 26L24 24L30 26"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.6"
        />
        {/* Power button */}
        <circle cx="24" cy="36" r="2" stroke="currentColor" strokeWidth="1" />
      </svg>
    ),

    instant_pot: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Pot body */}
        <path
          d="M10 24C10 18 16 14 24 14C32 14 38 18 38 24V34C38 40 32 44 24 44C16 44 10 40 10 34V24Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Lid rim */}
        <path
          d="M8 24C8 18 15 12 24 12C33 12 40 18 40 24"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Lid handle */}
        <path d="M20 12V8H28V12" stroke="currentColor" strokeWidth="1.5" />
        {/* Steam valve */}
        <rect
          x="22"
          y="5"
          width="4"
          height="4"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        {/* Steam puffs */}
        <path
          d="M21 5C22 3 26 3 27 5"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.5"
        />
        {/* Side handles */}
        <path
          d="M10 30H6M38 30H42"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Display panel */}
        <rect
          x="18"
          y="30"
          width="12"
          height="7"
          rx="1"
          stroke="currentColor"
          strokeWidth="1"
        />
      </svg>
    ),

    slow_cooker: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Pot body */}
        <path
          d="M6 24C6 18 14 12 24 12C34 12 42 18 42 24V32C42 38 34 44 24 44C14 44 6 38 6 32V24Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Lid */}
        <path
          d="M6 24C6 18 14 12 24 12C34 12 42 18 42 24"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Lid knob */}
        <path d="M21 12V8H27V12" stroke="currentColor" strokeWidth="1.5" />
        <circle
          cx="24"
          cy="7"
          r="2.5"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        {/* Side handles */}
        <path
          d="M6 28C4 28 2 26 2 24C2 22 4 20 6 20"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M42 28C44 28 46 26 46 24C46 22 44 20 42 20"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Temp indicators */}
        <circle cx="20" cy="38" r="1.5" fill="currentColor" opacity="0.4" />
        <circle cx="24" cy="40" r="1.5" fill="currentColor" opacity="0.4" />
        <circle cx="28" cy="38" r="1.5" fill="currentColor" opacity="0.4" />
      </svg>
    ),

    air_fryer: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Main body */}
        <rect
          x="8"
          y="6"
          width="32"
          height="36"
          rx="6"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Control panel */}
        <rect
          x="12"
          y="10"
          width="24"
          height="10"
          rx="2"
          stroke="currentColor"
          strokeWidth="1"
        />
        {/* Display */}
        <rect
          x="14"
          y="12"
          width="12"
          height="6"
          rx="1"
          stroke="currentColor"
          strokeWidth="1"
          fill="currentColor"
          fillOpacity="0.08"
        />
        {/* Knob */}
        <circle cx="32" cy="15" r="3" stroke="currentColor" strokeWidth="1.2" />
        {/* Basket drawer */}
        <rect
          x="12"
          y="24"
          width="24"
          height="14"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Basket handle */}
        <path d="M20 24V20H28V24" stroke="currentColor" strokeWidth="1.5" />
        {/* Basket vents */}
        <path
          d="M16 30h4M22 30h4M28 30h4M16 34h4M22 34h4M28 34h4"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.5"
        />
      </svg>
    ),

    // ── Diets ──────────────────────────────────────────────────────────

    omnivore: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Plate */}
        <circle
          cx="24"
          cy="30"
          r="14"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle
          cx="24"
          cy="30"
          r="10"
          stroke="currentColor"
          strokeWidth="0.75"
          opacity="0.4"
        />
        {/* Fork tines */}
        <path
          d="M10 6V12M12 6V12M14 6V12"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        {/* Fork neck */}
        <path
          d="M12 12V22"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Knife */}
        <path
          d="M36 6C38 8 38 14 36 18V22"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    vegetarian: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Leaf outline */}
        <path
          d="M24 42C24 42 8 34 8 20C8 10 16 6 24 6C32 6 40 10 40 20C40 34 24 42 24 42Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Midrib */}
        <path
          d="M24 42V6"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        {/* Lateral veins */}
        <path
          d="M24 16L16 20M24 22L14 26M24 28L16 32"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.7"
        />
        <path
          d="M24 16L32 20M24 22L34 26M24 28L32 32"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.7"
        />
      </svg>
    ),

    vegan: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Stem */}
        <path
          d="M24 44V28"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Left cotyledon leaf */}
        <path
          d="M24 32C20 30 12 30 10 24C10 24 16 20 24 28"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Right cotyledon leaf */}
        <path
          d="M24 28C32 20 38 24 38 24C36 30 28 30 24 32"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Central upright bud */}
        <path
          d="M24 28C22 22 22 14 24 10C26 14 26 22 24 28"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),

    pescatarian: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Fish body */}
        <path
          d="M6 26C6 19 12 14 22 14C32 14 40 19 40 26C40 33 32 38 22 38C12 38 6 33 6 26Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Tail fin */}
        <path
          d="M40 20L46 16V36L40 32"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Eye */}
        <circle cx="15" cy="25" r="2.5" fill="currentColor" />
        {/* Gill line */}
        <path
          d="M21 15C21 19 21 33 21 37"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.5"
        />
        {/* Dorsal fin */}
        <path
          d="M26 14C28 10 32 10 32 14"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    ),

    // ── Expertise ──────────────────────────────────────────────────────

    express: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Clock face */}
        <circle
          cx="24"
          cy="26"
          r="16"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Hands showing urgency */}
        <path
          d="M24 14V26L32 32"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Center dot */}
        <circle cx="24" cy="26" r="2" fill="currentColor" />
        {/* Tick marks */}
        <path
          d="M24 12V10M34 15L36 13M38 26H40M34 37L36 39M24 40V42M14 37L12 39M10 26H8M14 15L12 13"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        {/* Crown */}
        <path
          d="M22 10H26"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M24 10V8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    normal: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Frying pan */}
        <circle
          cx="19"
          cy="26"
          r="13"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Handle */}
        <path
          d="M32 22h12v8H32"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),

    chef: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Toque blanche — chef hat */}
        {/* Puffy top (wider than brim) */}
        <path
          d="M12 26C12 16 17 8 24 8C31 8 36 16 36 26"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Puff fold lines */}
        <path
          d="M18 26C18 20 20 13 24 10"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
        />
        <path
          d="M30 26C30 20 28 13 24 10"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
        />
        {/* Hat band */}
        <rect
          x="12"
          y="26"
          width="24"
          height="6"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {/* Cuff/lower section */}
        <path
          d="M12 32h24v6H12z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),

    // ── Household ──────────────────────────────────────────────────────

    household_1: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        <circle cx="24" cy="14" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M12 36C12 28 17 24 24 24C31 24 36 28 36 36"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    household_2: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Left person */}
        <circle cx="16" cy="14" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M6 34C6 27 10 23 16 23C22 23 26 27 26 34"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Right person */}
        <circle cx="32" cy="14" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M22 34C22 27 26 23 32 23C38 23 42 27 42 34"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    household_3: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Center adult */}
        <circle cx="24" cy="11" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M14 30C14 23 18 20 24 20C30 20 34 23 34 30"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Left adult */}
        <circle
          cx="10"
          cy="18"
          r="4.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M2 34C2 29 5 26 10 26C15 26 18 29 18 34"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        {/* Right adult */}
        <circle
          cx="38"
          cy="18"
          r="4.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M30 34C30 29 33 26 38 26C43 26 46 29 46 34"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>
    ),

    household_4: (props) => (
      <svg viewBox="0 0 48 48" fill="none" {...props}>
        {/* Adult 1 (top left) */}
        <circle cx="14" cy="10" r="5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M6 26C6 21 9 18 14 18C19 18 22 21 22 26"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Adult 2 (top right) */}
        <circle cx="34" cy="10" r="5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M26 26C26 21 29 18 34 18C39 18 42 21 42 26"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Child 1 (bottom left) */}
        <circle cx="14" cy="34" r="4" stroke="currentColor" strokeWidth="1.2" />
        <path
          d="M7 44C7 40 10 38 14 38C18 38 21 40 21 44"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        {/* Child 2 (bottom right) */}
        <circle cx="34" cy="34" r="4" stroke="currentColor" strokeWidth="1.2" />
        <path
          d="M27 44C27 40 30 38 34 38C38 38 41 40 41 44"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>
    ),
  };

interface OnboardingIconProps extends SVGProps<SVGSVGElement> {
  /** Icon name — must match a key in the ICONS map. */
  name: IconName;
}

export function OnboardingIcon({
  name,
  className,
  ...props
}: OnboardingIconProps) {
  const Icon = ICONS[name as IconName];
  if (!Icon) return null;
  return <Icon className={className} {...props} />;
}

export type { IconName };
